import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X } from "lucide-react"

import { cn } from "@/lib/utils"

/** Is this event target inside a Google Places autocomplete dropdown?
 *
 * Google renders `.pac-container` at the end of <body>, outside the dialog, so Radix
 * sees a click on a suggestion as a click OUTSIDE and closes the dialog underneath the
 * merchant mid-selection. The two handlers below suppress that.
 *
 * Defensive about the target because an interact-outside does not always report an
 * Element - it can be the document, or a text node - and `.closest` exists on neither.
 * An exception here escapes before Radix's own close handling runs.
 */
const isInsidePlacesDropdown = (target) => {
  try {
    return !!(target && typeof target.closest === "function" && target.closest(".pac-container"));
  } catch {
    return false;
  }
};

const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

const DialogOverlay = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-black/80  data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    {...props} />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

const DialogContent = React.forwardRef(({ className, children, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay />
    <DialogPrimitive.Content
      ref={ref}
      // Both guards are wrapped because e.target is not always an Element - an
      // interact-outside can report the document or a text node, and `.closest` is not
      // defined on either. A throw in here escapes before Radix runs its own close
      // handling, which is one way a dialog ends up refusing to shut (round 7, SP-30).
      onPointerDownOutside={(e) => {
        // Prevent dialog from closing when clicking on Google Places autocomplete dropdown
        if (isInsidePlacesDropdown(e.target)) {
          e.preventDefault();
        }
      }}
      onInteractOutside={(e) => {
        // Also prevent interact outside for the pac-container
        if (isInsidePlacesDropdown(e.target)) {
          e.preventDefault();
        }
      }}
      className={cn(
        // max-h and overflow-y are NOT cosmetic, and their absence was a P0 (round 7,
        // SP-37). This is `position: fixed` and vertically centred, so a dialog taller
        // than the viewport overflows off-screen in BOTH directions - and being fixed,
        // the page cannot scroll to reach either end. Whatever sits at the bottom
        // becomes unreachable.
        //
        // Stripe's card form is the tallest dialog in the app, so it failed hardest:
        // the Pay button was off-screen and Joel could not complete a payment at all,
        // which blocked every test downstream of launch. The portrait creative preview
        // failed the same way (SP-30) and read as a modal that would not close.
        //
        // Fixed here rather than in those two callers because every dialog has the
        // fault; several already carry their own max-h, which still wins over this one.
        "fixed left-[50%] top-[50%] z-50 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] max-h-[calc(100vh-2rem)] overflow-y-auto gap-4 border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg",
        className
      )}
      {...props}>
      {children}
      <DialogPrimitive.Close
        className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
        <X className="h-4 w-4" />
        <span className="sr-only">Close</span>
      </DialogPrimitive.Close>
    </DialogPrimitive.Content>
  </DialogPortal>
))
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({
  className,
  ...props
}) => (
  <div
    className={cn("flex flex-col space-y-1.5 text-center sm:text-left", className)}
    {...props} />
)
DialogHeader.displayName = "DialogHeader"

const DialogFooter = ({
  className,
  ...props
}) => (
  <div
    className={cn("flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2", className)}
    {...props} />
)
DialogFooter.displayName = "DialogFooter"

const DialogTitle = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn("text-lg font-semibold leading-none tracking-tight", className)}
    {...props} />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props} />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
